<template>
  <TransitionGroup
    class="relative bg-signableBrandColours-white list-none pl-0 mb-md border-solid border-1 border-l-0 border-b-0 border-r-0"
    v-show="value.length"
    name="list"
    tag="ol"
    ref="el"
  >
    <li
      v-for="(item, index) in value"
      :key="item.id"
      class="orderableListItem flex items-center py-sm border-solid border-1 border-l-0 border-t-0 border-r-0"
      :aria-label="item.name"
      :data-test="`list-item-${index}`"
    >
      <div class="flex ml-sm gap-base items-center">
        <ChevronControls
          :slimline="slimlineControls"
          :disabled="value.length === 1"
          @up="moveUp(index)"
          @down="moveDown(index)"
          :up-button-aria-label="
            $t('library.controls.chevronControls.moveUp', {
              item: item.name,
              index: index + 1,
              total: value.length,
            })
          "
          :down-button-aria-label="
            $t('library.controls.chevronControls.moveDown', {
              item: item.name,
              index: index + 1,
              total: value.length,
            })
          "
        />
        <slot :item="item" />
      </div>
    </li>
  </TransitionGroup>
</template>

<script>
// @ts-check
import { defineComponent, ref } from 'vue';
import { useSortable } from '@vueuse/integrations/useSortable';

import { ChevronControls } from '@/components/library/controls/ChevronControls';

/**
 * @typedef Item
 * @property {string} id
 * @property {string} name
 */

export default defineComponent({
  components: { ChevronControls },
  props: {
    /** @type {import('vue').PropOptions<Item[]>} */
    value: {
      type: Array,
      required: true,
    },
    slimlineControls: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const el = ref(null);

    /**
     * Emits the updated list with the item moved from index to newIndex.
     *
     * @param {number} index - The current index of the item being moved.
     * @param {number} newIndex - The new index where the item will be moved to.
     */
    function emitMoved(index, newIndex) {
      const moved = props.value.slice();
      moved.splice(newIndex, 0, moved.splice(index, 1)[0]);

      emit('input', moved);
    }

    useSortable(el, props.value, {
      handle: '.handle',
      animation: 200,
      forceFallback: true,
      onUpdate: (event) => {
        if (event.oldIndex === undefined || event.newIndex === undefined) {
          return;
        }

        emitMoved(event.oldIndex, event.newIndex);
      },
    });

    /**
     * Moves the item at the given index up one position in the list.
     *
     * @param {number} index - The index of the item to move up.
     */
    function moveUp(index) {
      emitMoved(index, index - 1);
    }

    /**
     * Moves the item at the given index down one position in the list.
     *
     * @param {number} index - The index of the item to move down.
     */
    function moveDown(index) {
      emitMoved(index, index + 1);
    }

    return {
      el,
      moveUp,
      moveDown,
    };
  },
});
</script>

<style scoped>
.orderableListItem {
  counter-increment: section;
}
.orderableListItem::before {
  content: counter(section);
  @apply text-signableBrandColours-darkGrey text-14;
}
.sortable-fallback.orderableListItem::before {
  content: '';
}
.sortable-fallback.orderableListItem {
  @apply border-b-0;
}
.list-move,
.list-enter-active,
.list-leave-active {
  @apply transition-all duration-200 ease-in-out;
}
.list-enter-from,
.list-leave-to {
  @apply opacity-0 translate-x-lg;
}
.list-leave-active {
  @apply absolute;
}
</style>
